import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    MenuItem,
    Box,
    Typography,
    Slide,
    InputBase,
    TextField,
    Autocomplete
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { addTaxDetails, getTaxDetails } from '../../Services/authService';
import { useThemeContext } from '../../Context/ThemeContext';
import { DARKCOLORS, LIGHTCOLORS } from '../../Utils/constants';
import SuccessNotification from '../../Components/Common/SuccessNotification';
import { StyledTextField } from '../../Components/Common/StyledTextField';
import LoadingAnimation from '../../Components/Common/LoadingAnimation';
import countryCodes from '../../Components/Layouts/full_country_calling_codes.json';

// Slide Transition Function
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} timeout={800} />;
});

function TaxModal({ open, onClose }) {
    const { t } = useTranslation();
    const { mode } = useThemeContext();

    const btnColor = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
    const modalBackgrounColor = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
    const inputBoxColor = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
    const errorTextColor = mode === 'dark' ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;
    const borderColor2 = mode === 'dark' ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale30;

    const [formData, setFormData] = useState({
        companyName: '',
        // taxId: '',
        email: '',
        phoneNumber: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        filingDuration: '',
        filingDate: '',
    });

    const [errors, setErrors] = useState({});
    const [generalError, setGeneralError] = useState('');
    const [successNotification, setSuccessNotification] = useState(false);
    const [loading, setLoading] = useState(false);
    const [countryCode, setCountryCode] = useState({ name: "Germany", dial_code: "+49" });
    const [phoneNumber, setPhoneNumber] = useState('');

    useEffect(() => {
        setGeneralError('')
        setErrors({})
        const fetchTaxData = async () => {
            try {
                setLoading(true);
                const data = await getTaxDetails();
                if (data) {
                    setFormData({
                        id: data.id || '',
                        companyName: data.companyName || '',
                        // taxId: data.taxId || '',
                        email: data.email || '',
                        phoneNumber: data.phoneNumber || '',
                        addressLine1: data.addressLine1 || '',
                        addressLine2: data.addressLine2 || '',
                        city: data.city || '',
                        state: data.state || '',
                        zipCode: data.zipCode || '',
                        country: data.country || '',
                        filingDuration: data.filingDuration || '',
                        filingDate: data.filingDate || ''
                    });
                    if (data?.phoneNumber) {
                        const matched = countryCodes.find(c => data.phoneNumber.startsWith(c.dial_code));
                        if (matched) {
                            setCountryCode(matched);
                            setPhoneNumber(data.phoneNumber.replace(matched.dial_code, '').trim());
                        } else {
                            setPhoneNumber(data.phoneNumber);
                        }
                    }
                }
            } catch (error) {
                console.error(t('taxes.errorFetchingTaxDetails'), error);
            } finally {
                setLoading(false);
            }
        };

        if (open) {
            fetchTaxData();
        }
    }, [open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.companyName) newErrors.companyName = t('taxes.CompanyName');
        // if (!formData.taxId) newErrors.taxId = 'Tax Identification Number is required.';


        // Email validation
        if (!formData.email) {
            newErrors.email = t('membershipCards.Emailisrequired');
        } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
            newErrors.email = t('membershipCards.Invalidemail');
        }

        // Phone number validation
        if (!phoneNumber) {
            newErrors.phoneNumber = t('taxes.MobileNumberrequired');
        } else if (!/^\d{7,15}$/.test(phoneNumber)) {
            newErrors.phoneNumber = t('taxes.MobileNumbermust');
        }

        if (!formData.addressLine1) newErrors.addressLine1 = t('membershipCards.Addressisrequired');
        if (!formData.filingDate) newErrors.startDate = t('taxes.Filingdaterequired');
        if (!formData.filingDuration) newErrors.filingDuration = t('taxes.FilingDuration');
        if (!formData.city) newErrors.city = t('membershipCards.Cityisrequired');
        if (!formData.state) newErrors.state = t('membershipCards.Stateisrequired');
        if (!formData.country) newErrors.country = t('taxes.Countryisrequired');
        if (!formData.zipCode) newErrors.zipCode = t('membershipCards.ZipCoderequired');
        else
            if (formData.zipCode && !/^\d{5,6}$/.test(formData.zipCode)) newErrors.zipCode = t('membershipCards.ZipCodemust');

        setErrors(newErrors);
        setGeneralError(
            Object.keys(newErrors)?.length > 0
                ? t('membershipCards.Pleasefill')
                : ''
        );
        return Object.keys(newErrors)?.length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);
            formData.phoneNumber = `${countryCode.dial_code} ${phoneNumber}`.trim();
            await addTaxDetails(formData);
            setSuccessNotification(true);
            onClose(); // Close modal after success
        } catch (error) {
            setGeneralError(error?.response?.data?.detail || t('taxes.Failedsavetax'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {loading && <LoadingAnimation />}
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="sm"
                TransitionComponent={Transition}
                PaperProps={{
                    sx: {
                        position: 'absolute',
                        right: 10,
                        margin: 0,
                        height: '100vh', // Full height
                        borderRadius: 3,
                        background: modalBackgrounColor,
                        width: "97%",
                        "@media (max-width: 427px)": {
                            right: 5,
                        },
                    },
                }}
            >
                {/* Title with Close Icon */}
                <DialogTitle sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    p: {
                        xs: 2,  // base padding
                        sm: 3,  // small screens
                        md: "20px 24px ",
                    },
                }}>
                    <span className='franchise-heading-text'>{t('taxes.TaxDetails')}</span>
                    <IconButton onClick={onClose} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{
                    overflowY: 'auto', paddingTop: 2,
                    p: {
                        xs: 2,  // base padding
                        sm: 3,  // small screens
                        md: "20px 24px ",  // medium screens
                    }
                }}>
                    <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <StyledTextField
                            label={t('taxes.NameofCompany')}
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            error={errors.companyName}
                            inputBoxColor={inputBoxColor}
                        />
                        {/* <StyledTextField
                            label={t('taxes.TaxIdentificationNumber')}
                            name="taxId"
                            value={formData.taxId}
                            onChange={handleChange}
                            error={errors.taxId}
                            inputBoxColor={inputBoxColor}
                        /> */}
                        <StyledTextField
                            label={t('membershipCards.email')}
                            name="email"
                            value={formData.email}
                            inputBoxColor={inputBoxColor}
                            error={errors.email}
                            onChange={handleChange}
                            required
                        />
                        {/* <StyledTextField
                            label={t('membershipCards.mobileNumber')}
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            inputBoxColor={inputBoxColor}
                            error={errors.phoneNumber}
                            onChange={handleChange}
                            required
                        /> */}
                        <Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: '6px',
                                    overflow: 'hidden',
                                    backgroundColor: inputBoxColor,
                                    height: '50px',
                                }}
                            >
                                <Autocomplete
                                    options={countryCodes}
                                    getOptionLabel={(option) => `${option.name} (${option.dial_code})`}
                                    value={countryCode}
                                    onChange={(e, newValue) => newValue && setCountryCode(newValue)}
                                    isOptionEqualToValue={(option, value) => option.dial_code === value.dial_code}
                                    sx={{
                                        minWidth: 185,
                                        '& .MuiInputBase-root': {
                                            height: '48px',
                                            borderRight: `1px solid ${borderColor2}`,
                                            borderRadius: 0,
                                            backgroundColor: 'transparent',
                                            px: 2,
                                        },
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="standard"
                                            InputProps={{ ...params.InputProps, disableUnderline: true }}
                                        />
                                    )}
                                />
                                <InputBase
                                    placeholder={`${t('membershipCards.mobileNumber')} *`}
                                    value={phoneNumber}
                                    onChange={(e) => {
                                        const digitsOnly = e.target.value.replace(/\D/g, '');
                                        setPhoneNumber(digitsOnly);
                                    }}
                                    sx={{ px: 2, flex: 1, height: '100%' }}
                                />
                            </Box>

                            {errors.phoneNumber && (
                                <Typography color="error" variant="caption">
                                    {errors.phoneNumber}
                                </Typography>
                            )}
                        </Box>

                        <StyledTextField
                            label={t('membershipCards.addressLine1')}
                            name="addressLine1"
                            value={formData.addressLine1}
                            inputBoxColor={inputBoxColor}
                            error={errors.addressLine1}
                            onChange={handleChange}
                            required
                        />
                        <StyledTextField
                            label={t('membershipCards.addressLine2')}
                            name="addressLine2"
                            value={formData.addressLine2}
                            inputBoxColor={inputBoxColor}
                            onChange={handleChange}
                        />
                        <StyledTextField
                            label={t('membershipCards.city')}
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            inputBoxColor={inputBoxColor}
                            error={errors.city}
                            required
                        />
                        <StyledTextField
                            label={t('membershipCards.state')}
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            inputBoxColor={inputBoxColor}
                            error={errors.state}
                            required
                        />
                        <StyledTextField
                            label={t('taxes.Country')}
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            inputBoxColor={inputBoxColor}
                            error={errors.country}
                            required
                        />
                        <StyledTextField
                            label={t('membershipCards.zipCode')}
                            name="zipCode"
                            numberType='number'
                            value={formData.zipCode}
                            onChange={handleChange}
                            inputBoxColor={inputBoxColor}
                            error={errors.zipCode}
                            required
                        />
                        {errors.zipCode && (
                            <Typography sx={{ color: errorTextColor, fontSize: '12px', marginTop: '-10px' }}>
                                {errors.zipCode}
                            </Typography>
                        )}

                        {/* Contact Details */}
                        <Typography variant="subtitle1" id="machineStreet" className='add-machine'>
                            {t('taxes.FilingDetails')}
                        </Typography>
                        {/* Dropdowns for Card Type */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <StyledTextField
                                label={t('taxes.Filingduration')}
                                name="filingDuration"
                                value={formData.filingDuration}
                                onChange={handleChange}
                                error={errors.filingDuration}
                                inputBoxColor={inputBoxColor}
                                required='required'
                            >
                                <MenuItem value="MONTHLY">{t('taxes.MONTHLY')}</MenuItem>
                                <MenuItem value="QUARTERLY">{t('taxes.QUARTERLY')}</MenuItem>
                                <MenuItem value="YEARLY">{t('taxes.YEARLY')}</MenuItem>
                            </StyledTextField>
                        </Box>
                        {/* Filinf Date */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <StyledTextField
                                label={t('taxes.FilingDate')}
                                name="filingDate"
                                value={formData.filingDate}
                                onChange={handleChange}
                                error={errors.filingDate}
                                required
                                isDate // Pass isDate to handle date-specific styling and behavior
                                inputBoxColor={inputBoxColor}
                            />
                        </Box>

                        {generalError && (
                            <Typography color="error" variant="body2" sx={{ textAlign: "center", marginTop: 1 }}>
                                {generalError}
                            </Typography>
                        )}
                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <button type="button" style={{ color: btnColor, borderColor: btnColor }} className='cancel-btn' onClick={onClose}>{t('membershipCards.cancel')}</button>
                            <button type="button" style={{ background: btnColor }} className='save-btn' onClick={handleSave}>{t('membershipCards.save')}</button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Use Notification Component */}
            <SuccessNotification
                open={successNotification}
                message={t('membershipCards.successMessage')}
                severity="success"
                onClose={() => setSuccessNotification(false)}
            />
        </>
    )
}

export default TaxModal